export type BikeLineMedia =
  | { type: 'video'; src: string; poster?: string; credit: string; sourceUrl?: string; playbackRate?: number; objectPosition?: string }
  | { type: 'image'; src: string; credit: string; sourceUrl?: string; objectPosition?: string }
  | { type: 'abstract'; credit: string; sourceUrl?: string }

export interface BikeLineBranding {
  frameId: string
  mark: string
  designation: string
  /** Roman-numeral series mark shown in the gold badge, e.g. "I" or "E-I" */
  series: string
  /** Gold line of the platform designation, e.g. "DH" or "Enduro" */
  platformShort: string
  /** Grey line of the platform designation, e.g. "Downhill Platform" */
  platformLabel: string
  cue: string
  tone: string
  accent: string
  bikeImage: string
  imageNote?: string
  media: BikeLineMedia
}

export const BIKE_LINE_BRANDING: Record<string, BikeLineBranding> = {
  'felon-i': {
    frameId: 'felon-i',
    mark: 'FELON',
    designation: 'I / DH',
    series: 'I',
    platformShort: 'DH',
    platformLabel: 'Downhill Platform',
    cue: 'Low, heavy, and hard to shake.',
    tone: 'The downhill bike gets the darkest identity treatment: compressed stance, hard edges, and a pressure-built feel.',
    accent: '#A3342F',
    bikeImage: '/images/bike-lines/hidden-felon.png',
    imageNote: 'Felon I is coming soon. Hidden placeholder image shown until release photography is ready.',
    media: {
      type: 'video',
      src: '/media/bike-lines/felon-user.mp4',
      credit: 'Felon ambient motion',
      playbackRate: 0.85,
      objectPosition: 'center center',
    },
  },
  'raptor-i': {
    frameId: 'raptor-i',
    mark: 'RAPTOR',
    designation: 'I / ENDURO',
    series: 'I',
    platformShort: 'Enduro',
    platformLabel: 'Platform',
    cue: 'Cutting-edge engineering, composed under load.',
    tone: 'A quiet F-22 reference sits behind the Raptor page: the pinnacle of its class, precise and technically uncompromising without shouting over the bike.',
    accent: '#C9A84C',
    bikeImage: '/images/raptor/raptor-frame-render.png',
    media: {
      type: 'video',
      src: '/media/bike-lines/f22-raptor-user-slo-mo.mp4',
      credit: 'Raptor ambient motion',
      playbackRate: 0.9,
      objectPosition: 'center center',
    },
  },
  'typhoon-i': {
    frameId: 'typhoon-i',
    mark: 'TYPHOON',
    designation: 'I / DOWN-COUNTRY',
    series: 'I',
    platformShort: 'Down-Country',
    platformLabel: 'Platform',
    cue: 'Advanced engineering, efficient and all-weather capable.',
    tone: 'The Typhoon page uses a clipped airshow motion reference: cutting-edge, refined, and atmospheric for the down-country line.',
    accent: '#7BA7A5',
    bikeImage: '/images/bike-lines/hidden-typhoon.png',
    imageNote: 'Typhoon I is coming soon. Hidden placeholder image shown until release photography is ready.',
    media: {
      type: 'video',
      src: '/media/bike-lines/typhoon-user.mp4',
      credit: 'Typhoon ambient motion',
      playbackRate: 0.9,
      objectPosition: 'center center',
    },
  },
  raider: {
    frameId: 'raider',
    mark: 'RAIDER',
    designation: 'E-I / E-ENDURO',
    series: 'E-I',
    platformShort: 'E-Enduro',
    platformLabel: 'Platform',
    cue: 'Long range, low profile, engineered to disappear.',
    tone: 'Raider borrows a subtle B-21 cue — the pinnacle of its generation: range, quiet mass, and an unmistakable silhouette, kept as a restrained motion panel.',
    accent: '#9FA7B3',
    bikeImage: '/images/bike-lines/hidden-raider.png',
    imageNote: 'Raider is coming soon. Hidden placeholder image shown until release photography is ready.',
    media: {
      type: 'video',
      src: '/media/bike-lines/b21-raider-user.mp4',
      credit: 'Raider ambient motion',
      playbackRate: 0.9,
      objectPosition: 'center center',
    },
  },
}
